/**
 * RFNetworkDashboard - Professional RF Device Network Control Dashboard
 * Displays discovered devices, real-time telemetry, and frequency control
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wifi,
  WifiOff,
  Radio,
  Signal,
  Battery,
  Volume2,
  RefreshCw,
  Search,
  Settings,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Server,
  Plus,
  Trash2,
  Edit2,
  Zap,
  Activity,
  Monitor,
  CircleDot,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AgentPanel from './AgentPanel';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Brand colors and logos
const BRAND_CONFIG = {
  shure: {
    color: '#E31837',
    bgColor: 'rgba(227, 24, 55, 0.1)',
    borderColor: 'rgba(227, 24, 55, 0.3)',
    name: 'Shure'
  },
  sennheiser: {
    color: '#00A0E3',
    bgColor: 'rgba(0, 160, 227, 0.1)',
    borderColor: 'rgba(0, 160, 227, 0.3)',
    name: 'Sennheiser'
  }
};

// Status indicators
const STATUS_CONFIG = {
  online: { color: '#34C759', icon: Wifi, text: 'En Linea' },
  offline: { color: '#FF3B30', icon: WifiOff, text: 'Sin Conexion' },
  connecting: { color: '#FFCC00', icon: Loader2, text: 'Conectando' },
  error: { color: '#FF3B30', icon: AlertTriangle, text: 'Error' },
  warning: { color: '#FFCC00', icon: AlertTriangle, text: 'Advertencia' }
};

// RF Level meter component
function RFLevelMeter({ level, showValue = true }) {
  const normalizedLevel = level !== null && level !== undefined 
    ? Math.max(0, Math.min(100, (level + 100) * 1.25)) // -100 to -20 dBm range
    : 0;
  
  const getColor = () => {
    if (level === null || level === undefined) return '#52525B';
    if (level > -40) return '#34C759';  // Strong signal
    if (level > -60) return '#FFCC00';  // Medium signal
    if (level > -80) return '#FF9500';  // Weak signal
    return '#FF3B30';  // Very weak
  };

  return (
    <div className="flex items-center gap-2">
      <Signal className="w-4 h-4" style={{ color: getColor() }} />
      <div className="flex-1 h-2 bg-[#27272A] rounded-full overflow-hidden">
        <div 
          className="h-full transition-all duration-300"
          style={{ 
            width: `${normalizedLevel}%`,
            backgroundColor: getColor()
          }}
        />
      </div>
      {showValue && (
        <span className="text-xs font-mono w-14 text-right" style={{ color: getColor() }}>
          {level !== null && level !== undefined ? `${level} dBm` : '--'}
        </span>
      )}
    </div>
  );
}

// Audio Level meter component
function AudioLevelMeter({ level, showValue = true }) {
  const normalizedLevel = level !== null && level !== undefined 
    ? Math.max(0, Math.min(100, (level + 60) * 1.67)) // -60 to 0 dB range
    : 0;
  
  const getColor = () => {
    if (level === null || level === undefined) return '#52525B';
    if (level > -6) return '#FF3B30';   // Clipping danger
    if (level > -12) return '#FFCC00';  // Hot
    if (level > -24) return '#34C759';  // Good
    return '#52525B';  // Low
  };

  return (
    <div className="flex items-center gap-2">
      <Volume2 className="w-4 h-4" style={{ color: getColor() }} />
      <div className="flex-1 h-2 bg-[#27272A] rounded-full overflow-hidden">
        <div 
          className="h-full transition-all duration-300"
          style={{ 
            width: `${normalizedLevel}%`,
            backgroundColor: getColor()
          }}
        />
      </div>
      {showValue && (
        <span className="text-xs font-mono w-12 text-right" style={{ color: getColor() }}>
          {level !== null && level !== undefined ? `${level} dB` : '--'}
        </span>
      )}
    </div>
  );
}

// Battery indicator component
function BatteryIndicator({ percent }) {
  const getColor = () => {
    if (percent === null || percent === undefined) return '#52525B';
    if (percent > 50) return '#34C759';
    if (percent > 20) return '#FFCC00';
    return '#FF3B30';
  };

  return (
    <div className="flex items-center gap-1">
      <Battery className="w-4 h-4" style={{ color: getColor() }} />
      <span className="text-xs font-mono" style={{ color: getColor() }}>
        {percent !== null && percent !== undefined ? `${percent}%` : '--'}
      </span>
    </div>
  );
}

// Channel Card component
function ChannelCard({ channel, deviceId, onSetFrequency }) {
  const [editingFreq, setEditingFreq] = useState(false);
  const [newFreq, setNewFreq] = useState(channel.frequency_mhz?.toFixed(3) || '');
  const status = STATUS_CONFIG[channel.status] || STATUS_CONFIG.offline;

  const handleSaveFrequency = async () => {
    const freq = parseFloat(newFreq);
    if (isNaN(freq) || freq < 400 || freq > 800) {
      toast.error('Frecuencia invalida (400-800 MHz)');
      return;
    }
    await onSetFrequency(deviceId, channel.channel_id, freq);
    setEditingFreq(false);
  };

  return (
    <div className={`p-3 rounded-lg border transition-all ${
      channel.status === 'online' 
        ? 'border-[#34C759]/30 bg-[#34C759]/5' 
        : 'border-white/[0.05] bg-[#0A0A0A]'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CircleDot className="w-4 h-4" style={{ color: status.color }} />
          <span className="font-medium text-white">{channel.name || `CH ${channel.channel_id}`}</span>
        </div>
        <Badge 
          className="text-[9px]"
          style={{ 
            backgroundColor: `${status.color}20`,
            color: status.color
          }}
        >
          {status.text}
        </Badge>
      </div>

      {/* Frequency */}
      <div className="mb-3">
        {editingFreq ? (
          <div className="flex gap-2">
            <Input
              value={newFreq}
              onChange={(e) => setNewFreq(e.target.value)}
              className="h-8 bg-[#111111] border-white/20 text-white font-mono"
              placeholder="000.000"
            />
            <Button size="sm" onClick={handleSaveFrequency} className="h-8 bg-[#34C759]">
              <CheckCircle className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingFreq(false)} className="h-8">
              <XCircle className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <div 
            className="flex items-center gap-2 cursor-pointer group"
            onClick={() => setEditingFreq(true)}
          >
            <Radio className="w-4 h-4 text-[#34C759]" />
            <span className="text-lg font-mono text-[#34C759]">
              {channel.frequency_mhz?.toFixed(3) || '---'} MHz
            </span>
            <Edit2 className="w-3 h-3 text-[#52525B] opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        )}
      </div>

      {/* Group/Bank */}
      {channel.group && (
        <div className="text-xs text-[#71717A] mb-2">
          Grupo: <span className="text-white">{channel.group}</span>
        </div>
      )}

      {/* Meters */}
      <div className="space-y-2">
        <RFLevelMeter level={channel.rf_level_dbm} />
        <AudioLevelMeter level={channel.audio_level_db} />
        <div className="flex items-center justify-between">
          <BatteryIndicator percent={channel.battery_percent} />
          {channel.tx_type && (
            <span className="text-xs text-[#71717A]">{channel.tx_type}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Device Card component
function DeviceCard({ device, expanded, onToggle, onRefresh, onDelete, onSetFrequency }) {
  const brand = BRAND_CONFIG[device.brand] || { color: '#71717A', bgColor: 'rgba(113,113,122,0.1)', name: device.brand };
  const status = STATUS_CONFIG[device.connection_status] || STATUS_CONFIG.offline;
  const StatusIcon = status.icon;

  return (
    <Card 
      className="border transition-all"
      style={{ 
        borderColor: expanded ? brand.color : 'rgba(255,255,255,0.05)',
        backgroundColor: expanded ? brand.bgColor : '#111111'
      }}
    >
      <CardHeader 
        className="cursor-pointer py-3"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-[#71717A]" />
            ) : (
              <ChevronRight className="w-4 h-4 text-[#71717A]" />
            )}
            
            {/* Status indicator */}
            <div 
              className="w-3 h-3 rounded-full animate-pulse"
              style={{ backgroundColor: status.color }}
            />
            
            {/* Brand badge */}
            <Badge 
              className="text-[10px] px-2"
              style={{ 
                backgroundColor: brand.bgColor,
                color: brand.color,
                borderColor: brand.borderColor
              }}
            >
              {brand.name}
            </Badge>
            
            {/* Device info */}
            <div>
              <h3 className="font-medium text-white">{device.device_name || device.model}</h3>
              <p className="text-xs text-[#71717A]">
                {device.model} • {device.ip}:{device.port}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Channel count */}
            <Badge className="bg-[#27272A] text-[#A1A1AA]">
              {device.channels?.length || 0} CH
            </Badge>
            
            {/* Connection status */}
            <div className="flex items-center gap-1 px-2 py-1 rounded" style={{ backgroundColor: `${status.color}20` }}>
              <StatusIcon className="w-3 h-3" style={{ color: status.color }} />
              <span className="text-xs" style={{ color: status.color }}>{status.text}</span>
            </div>

            {/* Actions */}
            <Button 
              size="sm" 
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onRefresh(device.id); }}
              className="h-8 w-8 p-0"
            >
              <RefreshCw className="w-4 h-4 text-[#71717A]" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onDelete(device.id); }}
              className="h-8 w-8 p-0 text-[#FF3B30] hover:text-[#FF3B30]"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          {/* Device details */}
          <div className="grid grid-cols-4 gap-4 mb-4 p-3 bg-[#0A0A0A] rounded-lg">
            <div>
              <p className="text-xs text-[#71717A]">Firmware</p>
              <p className="text-sm text-white font-mono">{device.firmware || '--'}</p>
            </div>
            <div>
              <p className="text-xs text-[#71717A]">Serial</p>
              <p className="text-sm text-white font-mono">{device.serial_number || '--'}</p>
            </div>
            <div>
              <p className="text-xs text-[#71717A]">Descubierto</p>
              <p className="text-sm text-white">{device.discovery_method || 'Manual'}</p>
            </div>
            <div>
              <p className="text-xs text-[#71717A]">Ultima conexion</p>
              <p className="text-sm text-white">
                {device.last_seen ? new Date(device.last_seen).toLocaleTimeString() : '--'}
              </p>
            </div>
          </div>

          {/* Channels grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {device.channels?.map((channel) => (
              <ChannelCard 
                key={channel.channel_id}
                channel={channel}
                deviceId={device.id}
                onSetFrequency={onSetFrequency}
              />
            ))}
          </div>

          {/* Capabilities */}
          <div className="mt-4 flex flex-wrap gap-2">
            {device.capabilities?.map((cap) => (
              <Badge key={cap} className="text-[9px] bg-[#27272A] text-[#71717A]">
                {cap.replace('_', ' ')}
              </Badge>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// Add Device Dialog
function AddDeviceDialog({ open, onOpenChange, onAdd }) {
  const [ip, setIp] = useState('');
  const [brand, setBrand] = useState('shure');
  const [port, setPort] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!ip) {
      toast.error('Ingresa una direccion IP');
      return;
    }

    setAdding(true);
    try {
      await onAdd(ip, brand, port ? parseInt(port) : null);
      onOpenChange(false);
      setIp('');
      setPort('');
    } catch (error) {
      // Error handled in parent
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#34C759]" />
            Agregar Dispositivo Manual
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-[#A1A1AA]">Direccion IP</Label>
            <Input
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              placeholder="192.168.1.100"
              className="mt-1 bg-[#0A0A0A] border-white/10 text-white"
            />
          </div>

          <div>
            <Label className="text-[#A1A1AA]">Marca</Label>
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger className="mt-1 bg-[#0A0A0A] border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111111] border-white/10">
                <SelectItem value="shure">Shure</SelectItem>
                <SelectItem value="sennheiser">Sennheiser</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[#A1A1AA]">Puerto (opcional)</Label>
            <Input
              value={port}
              onChange={(e) => setPort(e.target.value)}
              placeholder="2202"
              className="mt-1 bg-[#0A0A0A] border-white/10 text-white"
            />
            <p className="text-xs text-[#52525B] mt-1">
              Shure: 2202 • Sennheiser: 53212
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/20">
            Cancelar
          </Button>
          <Button onClick={handleAdd} disabled={adding} className="bg-[#34C759]">
            {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Agregar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Dashboard Component
export default function RFNetworkDashboard() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [expandedDevices, setExpandedDevices] = useState(new Set());
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const pollingRef = useRef(null);

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/rf-network/devices`, getAuthHeaders());
      setDevices(res.data.devices || []);
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and polling
  useEffect(() => {
    fetchDevices();
    
    // Start polling every 5 seconds
    pollingRef.current = setInterval(fetchDevices, 5000);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchDevices]);

  // Discovery
  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/rf-network/discover`,
        { timeout_seconds: 10 },
        getAuthHeaders()
      );
      
      if (res.data.count > 0) {
        toast.success(`Encontrados ${res.data.count} dispositivos`);
        await fetchDevices();
      } else {
        toast.info('No se encontraron nuevos dispositivos');
      }
    } catch (error) {
      toast.error('Error en el escaneo de red');
    } finally {
      setDiscovering(false);
    }
  };

  // Add manual device
  const handleAddManual = async (ip, brand, port) => {
    try {
      const res = await axios.post(
        `${API_BASE}/api/rf-network/add-manual`,
        { ip, brand, port },
        getAuthHeaders()
      );
      
      toast.success(`Dispositivo agregado: ${res.data.device.model}`);
      await fetchDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo conectar al dispositivo');
      throw error;
    }
  };

  // Refresh device status
  const handleRefresh = async (deviceId) => {
    try {
      await axios.get(`${API_BASE}/api/rf-network/devices/${deviceId}/status`, getAuthHeaders());
      await fetchDevices();
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error('Error actualizando estado');
    }
  };

  // Delete device
  const handleDelete = async (deviceId) => {
    if (!window.confirm('¿Eliminar este dispositivo?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/rf-network/devices/${deviceId}`, getAuthHeaders());
      toast.success('Dispositivo eliminado');
      await fetchDevices();
    } catch (error) {
      toast.error('Error eliminando dispositivo');
    }
  };

  // Set frequency
  const handleSetFrequency = async (deviceId, channelId, frequencyMhz) => {
    try {
      await axios.post(
        `${API_BASE}/api/rf-network/devices/${deviceId}/frequency`,
        { channel_id: channelId, frequency_mhz: frequencyMhz },
        getAuthHeaders()
      );
      toast.success(`Frecuencia cambiada a ${frequencyMhz.toFixed(3)} MHz`);
      await fetchDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error cambiando frecuencia');
    }
  };

  // Toggle expanded
  const toggleExpanded = (deviceId) => {
    setExpandedDevices(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
      } else {
        next.add(deviceId);
      }
      return next;
    });
  };

  // Filter devices
  const filteredDevices = devices.filter(device => {
    if (filter !== 'all' && device.brand !== filter) return false;
    if (searchQuery && !device.device_name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !device.model?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !device.ip?.includes(searchQuery)) return false;
    return true;
  });

  // Stats
  const onlineCount = devices.filter(d => d.connection_status === 'online').length;
  const totalChannels = devices.reduce((acc, d) => acc + (d.channels?.length || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#007AFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Monitor className="w-6 h-6 text-[#007AFF]" />
            Dispositivos RF en Red
          </h1>
          <p className="text-sm text-[#71717A]">
            Monitoreo y control de equipos Shure y Sennheiser conectados a la red
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleDiscover}
            disabled={discovering}
            className="bg-[#007AFF] hover:bg-[#007AFF]/90"
          >
            {discovering ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Search className="w-4 h-4 mr-2" />
            )}
            {discovering ? 'Escaneando...' : 'Escanear Red'}
          </Button>
          <Button
            onClick={() => setShowAddDialog(true)}
            variant="outline"
            className="border-white/20"
          >
            <Plus className="w-4 h-4 mr-2" />
            Agregar Manual
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-[#111111] border-white/[0.05]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#007AFF]/20 rounded-lg flex items-center justify-center">
              <Server className="w-5 h-5 text-[#007AFF]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{devices.length}</p>
              <p className="text-xs text-[#71717A]">Dispositivos</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-white/[0.05]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#34C759]/20 rounded-lg flex items-center justify-center">
              <Wifi className="w-5 h-5 text-[#34C759]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{onlineCount}</p>
              <p className="text-xs text-[#71717A]">En Linea</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-white/[0.05]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFCC00]/20 rounded-lg flex items-center justify-center">
              <Radio className="w-5 h-5 text-[#FFCC00]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{totalChannels}</p>
              <p className="text-xs text-[#71717A]">Canales</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-white/[0.05]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#AF52DE]/20 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 text-[#AF52DE]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {devices.filter(d => d.brand === 'shure').length} / {devices.filter(d => d.brand === 'sennheiser').length}
              </p>
              <p className="text-xs text-[#71717A]">Shure / Sennheiser</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
          <Input
            placeholder="Buscar por nombre, modelo o IP..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
          />
        </div>

        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-[#0A0A0A]">
            <TabsTrigger value="all" className="data-[state=active]:bg-[#27272A]">
              Todos
            </TabsTrigger>
            <TabsTrigger value="shure" className="data-[state=active]:bg-[#E31837]/20 data-[state=active]:text-[#E31837]">
              Shure
            </TabsTrigger>
            <TabsTrigger value="sennheiser" className="data-[state=active]:bg-[#00A0E3]/20 data-[state=active]:text-[#00A0E3]">
              Sennheiser
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Device List */}
      {filteredDevices.length === 0 ? (
        <Card className="bg-[#111111] border-white/[0.05]">
          <CardContent className="py-12 text-center">
            <Server className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
            <p className="text-[#71717A] mb-2">No hay dispositivos RF en red</p>
            <p className="text-sm text-[#52525B] mb-4">
              Usa "Escanear Red" para descubrir dispositivos automaticamente o agrega uno manualmente
            </p>
            <div className="flex justify-center gap-2">
              <Button onClick={handleDiscover} className="bg-[#007AFF]">
                <Search className="w-4 h-4 mr-2" />
                Escanear Red
              </Button>
              <Button onClick={() => setShowAddDialog(true)} variant="outline" className="border-white/20">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Manual
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredDevices.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              expanded={expandedDevices.has(device.id)}
              onToggle={() => toggleExpanded(device.id)}
              onRefresh={handleRefresh}
              onDelete={handleDelete}
              onSetFrequency={handleSetFrequency}
            />
          ))}
        </div>
      )}

      {/* Add Device Dialog */}
      <AddDeviceDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onAdd={handleAddManual}
      />

      {/* Agent Panel Section */}
      <div className="mt-8 pt-6 border-t border-white/10">
        <AgentPanel onDevicesUpdate={fetchDevices} />
      </div>
    </div>
  );
}
